import { FormEvent, useEffect, useState } from "react";
import {
  ApiError,
  OrgCategory,
  ORG_CATEGORY_LABELS,
  Organization,
  api,
} from "../lib/api";
import { useAuth } from "../lib/auth";

interface DemoDocument {
  index: number;
  filename: string;
  size_bytes: number;
}

interface DemoCredentials {
  email: string;
  password: string;
  display_name: string;
  org_name: string;
  org_id: number;
}

interface DemoCase {
  id: number;
  name: string;
  slug: string;
  description?: string | null;
  matter_number?: string | null;
}

interface DemoResult {
  demo_id: string;
  case: DemoCase;
  credentials: DemoCredentials;
  documents: DemoDocument[];
}

export default function AdminGenerateDemo() {
  const me = useAuth((s) => s.user);
  const isOwner = me?.role === "owner";

  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [orgChoice, setOrgChoice] = useState<string>("new");
  const [newOrgName, setNewOrgName] = useState("");
  const [category, setCategory] = useState<OrgCategory | "">("");
  const [scenario, setScenario] = useState("");

  const [generating, setGenerating] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [result, setResult] = useState<DemoResult | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  useEffect(() => {
    if (!isOwner) return;
    api.get<Organization[]>("/organizations").then(setOrgs).catch(() => {});
  }, [isOwner]);

  function handleOrgChange(value: string) {
    setOrgChoice(value);
    if (value !== "new") {
      const org = orgs.find((o) => o.id === Number(value));
      if (org?.category) {
        setCategory(org.category);
      }
    }
  }

  async function handleSubmit(ev: FormEvent) {
    ev.preventDefault();
    setErr(null);
    setResult(null);
    setGenerating(true);

    const payload: Record<string, unknown> = {
      scenario,
      category,
    };

    if (orgChoice === "new") {
      if (!newOrgName.trim()) {
        setErr("Please enter a name for the new organization.");
        setGenerating(false);
        return;
      }
      payload.org_name = newOrgName.trim();
    } else {
      payload.organization_id = Number(orgChoice);
    }

    try {
      const res = await api.post<DemoResult>("/admin/generate-demo", payload);
      setResult(res);
    } catch (e: unknown) {
      setErr(
        e instanceof ApiError
          ? e.message
          : "Failed to generate demo. Please try again."
      );
    } finally {
      setGenerating(false);
    }
  }

  function copyToClipboard(text: string, label: string) {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(label);
      setTimeout(() => setCopied((c) => (c === label ? null : c)), 2000);
    });
  }

  function downloadDoc(demoId: string, doc: DemoDocument) {
    const token = localStorage.getItem("cf_token");
    const base = import.meta.env.VITE_API_BASE ?? "/api";
    const url = `${base}/admin/generate-demo/download/${demoId}/${doc.index}`;
    const a = document.createElement("a");

    fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => r.blob())
      .then((blob) => {
        a.href = URL.createObjectURL(blob);
        a.download = doc.filename;
        a.click();
        URL.revokeObjectURL(a.href);
      });
  }

  function downloadAll(demoId: string, docs: DemoDocument[]) {
    for (const doc of docs) {
      downloadDoc(demoId, doc);
    }
  }

  if (!isOwner) {
    return (
      <div className="admin">
        <header className="admin__head">
          <div>
            <div className="page-eyebrow">Generate Demo</div>
            <h1>Generate Demo</h1>
          </div>
        </header>
        <div className="alert alert--error" role="alert">
          Demo generation is restricted to owners.
        </div>
      </div>
    );
  }

  return (
    <div className="admin">
      <header className="admin__head">
        <div>
          <div className="page-eyebrow">Site settings</div>
          <h1>Generate Demo</h1>
          <p className="admin__lead">
            Use AI to create a realistic demo case with downloadable documents.
            Choose an organization and describe your scenario — the system will
            generate the case, documents, and a demo admin account.
          </p>
        </div>
      </header>

      {err && <div className="alert alert--error">{err}</div>}

      {/* --- Configuration form --- */}
      <form className="admin-card" onSubmit={handleSubmit}>
        <div className="admin-card__head">
          <div>
            <h2 className="admin-card__title">Demo configuration</h2>
            <p className="admin-card__blurb">
              Select or create an organization, pick the industry category, and
              describe the scenario you'd like to demo.
            </p>
          </div>
        </div>

        <div className="admin-card__grid">
          <div className="admin-card__field admin-card__field--wide">
            <label htmlFor="dg-org">Organization</label>
            <select
              id="dg-org"
              value={orgChoice}
              onChange={(e) => handleOrgChange(e.target.value)}
              disabled={generating}
            >
              <option value="new">+ Create new organization</option>
              {orgs.map((org) => (
                <option key={org.id} value={String(org.id)}>
                  {org.name}
                  {org.id === me?.organization_id ? " (yours)" : ""}
                </option>
              ))}
            </select>
          </div>

          {orgChoice === "new" && (
            <div className="admin-card__field admin-card__field--wide">
              <label htmlFor="dg-org-name">New organization name</label>
              <input
                id="dg-org-name"
                type="text"
                required
                autoComplete="off"
                placeholder='e.g. "Acme Legal Services"'
                value={newOrgName}
                onChange={(e) => setNewOrgName(e.target.value)}
                disabled={generating}
              />
            </div>
          )}

          <div className="admin-card__field admin-card__field--wide">
            <label htmlFor="dg-category">Category</label>
            <select
              id="dg-category"
              value={category}
              onChange={(e) => setCategory(e.target.value as OrgCategory)}
              required
              disabled={generating}
            >
              <option value="" disabled>
                Select a category…
              </option>
              {Object.entries(ORG_CATEGORY_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
            <div className="admin-card__hint muted">
              The category shapes the type of case and documents generated.
            </div>
          </div>

          <div className="admin-card__field admin-card__field--wide">
            <label htmlFor="dg-scenario">Scenario</label>
            <textarea
              id="dg-scenario"
              required
              rows={5}
              minLength={10}
              placeholder={
                'Describe the demo scenario, e.g. "A personal injury case involving a car accident on Highway 101. The plaintiff is seeking damages for medical expenses and lost wages. Include police report, medical records, and insurance correspondence."'
              }
              value={scenario}
              onChange={(e) => setScenario(e.target.value)}
              disabled={generating}
              style={{ resize: "vertical", width: "100%" }}
            />
            <div className="admin-card__hint muted">
              Be as specific as you'd like — the AI will generate a realistic
              case and documents based on your description.
            </div>
          </div>
        </div>

        <div className="admin-card__foot">
          <div className="admin-card__meta muted">
            This will create a case, admin user, and downloadable documents.
          </div>
          <div className="admin-card__actions">
            <button
              type="submit"
              className="btn btn--primary"
              disabled={generating || !category || !scenario.trim()}
            >
              {generating ? "Generating demo…" : "Generate demo"}
            </button>
          </div>
        </div>
      </form>

      {/* --- Loading indicator --- */}
      {generating && (
        <div className="admin-card" style={{ textAlign: "center", padding: "2.5rem 2rem" }}>
          <div className="spinner spinner--lg" style={{ margin: "0 auto 1.25rem" }} />
          <p style={{ fontWeight: 600, marginBottom: "0.25rem" }}>
            Generating your demo…
          </p>
          <p className="muted" style={{ maxWidth: 420, margin: "0 auto" }}>
            The AI is creating a realistic case, admin account, and 3–5
            documents. This typically takes 30–60 seconds.
          </p>
        </div>
      )}

      {/* --- Results --- */}
      {result && (
        <>
          {/* Case summary */}
          <div className="admin-card">
            <div className="admin-card__head">
              <div>
                <h2 className="admin-card__title">Case created</h2>
              </div>
            </div>
            <div className="admin-card__grid">
              <div className="admin-card__field admin-card__field--wide">
                <label>Case name</label>
                <div style={{ padding: "0.5rem 0", fontWeight: 600 }}>
                  {result.case.name}
                </div>
              </div>
              {result.case.matter_number && (
                <div className="admin-card__field">
                  <label>Matter number</label>
                  <div style={{ padding: "0.5rem 0" }}>
                    {result.case.matter_number}
                  </div>
                </div>
              )}
              {result.case.description && (
                <div className="admin-card__field admin-card__field--wide">
                  <label>Description</label>
                  <div style={{ padding: "0.5rem 0" }}>
                    {result.case.description}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Credentials */}
          <div className="admin-card">
            <div className="admin-card__head">
              <div>
                <h2 className="admin-card__title">Demo admin account</h2>
                <p className="admin-card__blurb">
                  Use these credentials to log in as the demo admin for{" "}
                  <strong>{result.credentials.org_name}</strong>.
                </p>
              </div>
            </div>
            <div className="admin-card__grid">
              <div className="admin-card__field">
                <label>Name</label>
                <div style={{ padding: "0.5rem 0" }}>
                  {result.credentials.display_name}
                </div>
              </div>
              <div className="admin-card__field">
                <label>Email</label>
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.5rem 0" }}>
                  <code style={{ fontSize: "0.95em" }}>
                    {result.credentials.email}
                  </code>
                  <button
                    type="button"
                    className="btn btn--small"
                    onClick={() =>
                      copyToClipboard(result.credentials.email, "email")
                    }
                  >
                    {copied === "email" ? "Copied!" : "Copy"}
                  </button>
                </div>
              </div>
              <div className="admin-card__field">
                <label>Password</label>
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.5rem 0" }}>
                  <code style={{ fontSize: "0.95em" }}>
                    {result.credentials.password}
                  </code>
                  <button
                    type="button"
                    className="btn btn--small"
                    onClick={() =>
                      copyToClipboard(result.credentials.password, "password")
                    }
                  >
                    {copied === "password" ? "Copied!" : "Copy"}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Documents */}
          <div className="admin-card">
            <div className="admin-card__head">
              <div>
                <h2 className="admin-card__title">
                  Generated documents ({result.documents.length})
                </h2>
                <p className="admin-card__blurb">
                  Download these files, then upload them to the case through the
                  Documents tab to demo the full workflow.
                </p>
              </div>
              <div>
                <button
                  type="button"
                  className="btn btn--primary"
                  onClick={() => downloadAll(result.demo_id, result.documents)}
                >
                  Download all
                </button>
              </div>
            </div>
            <div className="admin__scroll">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Filename</th>
                    <th>Size</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {result.documents.map((doc) => (
                    <tr key={doc.index}>
                      <td className="admin-table__mono">{doc.filename}</td>
                      <td className="muted">
                        {(doc.size_bytes / 1024).toFixed(1)} KB
                      </td>
                      <td className="admin-table__actions">
                        <button
                          type="button"
                          className="btn"
                          onClick={() => downloadDoc(result.demo_id, doc)}
                        >
                          Download
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
